import useDeliveryServices from '@/hooks/useDeliveryServices';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getSearchResultsV31 } from '@/api/Search';
import React, { useState, useEffect, useCallback, useRef, useContext, useMemo } from 'react';
import {
  ProductCatalogueV2,
  SnackBar,
  OffersSnackbar,
  FilterChipsList,
  SortingAndFilters,
  Heading,
  HeadingEnum,
  BoldEnum,
  ExpandableSearchBar,
} from '@desquared/wave-components-library';
import useBundleOffersSummary from '@/hooks/useBundleOffersSummary';
import useProductTags from '@/hooks/useProductTags';
import { getProductsWithFixedCategory } from '@/utils/category';
import { useRouter } from 'next/router';
import CategoryTitle from '@/components/CategoryTitle/CategoryTitle';
import styles from './BrandCatalogue.module.css';
import { useTranslations } from 'next-intl';
import {
  addProduct,
  removeProduct,
  openListSideSheet,
  getBundleOfferDescription,
  getProductPropsWithCleanUrl,
  getProductBasedOnSlug,
  getProductIndexBasedOnSlug,
} from '@/utils/products';
import { getProductCategoriesBrand } from '@/api/Products';
import { checkListStatus } from '@/utils/lists';
import { productsForOrder } from '@/store/cart';
import EmptyStateImg from '../../../public/assets/wave-grocery/Empty State.png';
import { BundleOfferSnackBarOpen } from '@/store/bundleOfferSnackBar';
import { BundleOfferSideSheetStore } from '@/store/sideSheet';
import { SelectedBundleOfferStore } from '@/store/selectedBundleOfferId';
import useGetDefaultProductImage from '@/hooks/useGetDefaultProductImage';
import { omit } from 'lodash';
import FiltersAside from '@/components/FiltersAside/FiltersAside';
import useCategoryFilters from '@/hooks/useCategoryFilters.js';
import { SEARCH_SORT_OPTIONS } from '@/constants/sort.js';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { getV31Products } from '@/api/Products.js';
import { SORT_OPTIONS } from '@/constants/sort';
import FiltersFAB from '@/components/FiltersFAB/FiltersFAB';
import ScrollToTop from '@/components/ScrollToTop/ScrollToTop';
import CustomImage from '@/components/CustomImage/CustomImage.jsx';
import Link from 'next/link.js';
import { getProductInfo, selectItem } from '@/utils/analytics.js';
import { enhanceProductWithSlug } from '@/utils/products/productSlugs.js';
import { titleCaseString } from '@/utils/stringManipulationUtils.js';
import { usePersonalizedProductsIds } from '@/contexts/PersonalizedProductsIdsContext.jsx';
import { CurrencyContext } from '@/contexts/CurrencyProvider.jsx';

export function BrandCatalogue({ brand, categoriesFromFile, onSelect }) {
  const router = useRouter();
  const observer = useRef(null);
  const t = useTranslations();
  const a = useTranslations('common.asides');

  const [totalCount, setTotalCount] = useState(0);
  const [productsOrder, setProductsOrder] = useState([]);
  const [showSnackBar, setShowSnackbar] = useState(false);
  const [showBundleOffersSnackBar, setShowBundleOffersSnackBar] = useState(false);
  const defaultProductImage = useGetDefaultProductImage(0);
  const [filterChipsItems, setFilterChipsItems] = useState([]);
  const [isFiltersSheetOpen, setIsFiltersSheetOpen] = useState(false);
  const [selectedSort, setSelectedSort] = useState(router.query.sort || 0);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedQuery = useDebouncedValue(searchTerm, 500);
  const { getCollectionTypes, selectedService } = useDeliveryServices();
  const [collectionTypes, _] = useState(getCollectionTypes());
  const { personalizedProductsIds } = usePersonalizedProductsIds();
  const { formatter } = useContext(CurrencyContext);

  const {
    isLoading: isFiltersLoading,
    selectedFilters,
    handleSelectFilter,
    numberOfSelectedFilters,
    clearFilters,
    handleApplyFilters,
    getFiltersFromUrl,
  } = useCategoryFilters(router?.query?.category, {
    allowAttributes: false, // maybe this should be !!router?.query?.category cause theoretically we could have attrs and stuff when we have a category
    allowBrands: false,
    allowCategoryKinds: false,
    allowTags: false,
  });
  const privateApiKey = process.env.NODE_ENV === 'development' ? process.env.PRIVATE_API_KEY : null;

  const {
    data,
    fetchNextPage: fetchNextPageProducts,
    isLoading: isLoadingProducts,
    isFetchingNextPageProducts,
  } = useInfiniteQuery({
    queryKey: [
      'products',
      router.locale,
      getCollectionTypes(),
      selectedService,
      brand,
      router.query.subCategory,
      router.query,
      router.locale,
      selectedSort,
    ],
    queryFn: async ({ pageParam }) => {
      const { offerFilters } = getFiltersFromUrl();

      return getV31Products(
        {
          category: router.query.subCategory ? router.query.subCategory : null,
          collectionTypes: getCollectionTypes(),
          pageNumber: pageParam.toString(),
          pageSize: 20,
          brands: [brand],
          sort: [SORT_OPTIONS[selectedSort]],
          ...offerFilters,
        },
        router.locale,
        privateApiKey
      );
    },

    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage.length === 0) {
        return undefined;
      }
      return lastPageParam + 1;
    },
    initialPageParam: 0,
    staleTime: Infinity,
  });

  const {
    data: searchResultsPages,
    isLoading: isLoadingCatalogueSearch,
    isFetchingNextPage: isFetchingNextPageSearch,
    fetchNextPage: fetchNextPageSearch,
  } = useInfiniteQuery({
    queryKey: [
      'search-results',
      debouncedQuery,
      brand,
      router.query.subCategory,
      router.query,
      router.locale,
      selectedSort,
    ],
    enabled: debouncedQuery?.length >= 3,
    initialPageParam: 0,
    queryFn: ({ queryKey, pageParam }) => {
      const { offerFilters } = getFiltersFromUrl();
      if (debouncedQuery?.length >= 3) {
        return getSearchResultsV31({
          term: queryKey[1],
          brands: [brand],
          category: router.query.subCategory ? router.query.subCategory : null,
          pageNumber: pageParam.toString(),
          pageSize: 20,
          sort: [SEARCH_SORT_OPTIONS[selectedSort]],
          ...offerFilters,
        });
      }
    },
    getNextPageParam: (lastPage, pages) => {
      if (lastPage?.pageNumber + 1 >= lastPage?.totalPages) return undefined;

      return lastPage?.pageNumber + 1;
    },
    staleTime: Infinity,
  });

  const { bundleOffersSummary } = useBundleOffersSummary();
  const { productTags } = useProductTags();

  const { data: brandCategories, isLoading: isLoadingSuggestions } = useQuery({
    queryKey: ['brands', brand, router.locale],
    queryFn: ({ queryKey }) => getProductCategoriesBrand({ brands: [brand], locale: router.locale }),
  });

  useEffect(() => {
    if (router.isReady && brandCategories) {
      setFilterChipsItems([
        { text: t('category_details.all_categories'), selected: !router.query.subCategory },

        ...brandCategories.map(i => {
          return { text: i.name, ...i, selected: router.query.subCategory === i.id };
        }),
      ]);
    }
  }, [router.query, router.isReady, setFilterChipsItems, t, brandCategories, router.locale]);

  useEffect(() => {
    const productsForOrder$ = productsForOrder.subscribe(setProductsOrder);
    const BundleOfferSnackBarOpen$ = BundleOfferSnackBarOpen.subscribe(setShowBundleOffersSnackBar);
    return () => {
      productsForOrder$.unsubscribe();
      BundleOfferSnackBarOpen$.unsubscribe();
    };
  }, []);

  const handleSelectSubcategory = async item => {
    const url = { pathname: router.pathname };
    if (item.text === t('category_details.all_categories')) {
      url['query'] = omit({ ...router.query }, 'subCategory');
    } else if (item.selected) {
      url['query'] = omit({ ...router.query }, 'subCategory');
    } else {
      url['query'] = { ...router.query, subCategory: item.id };
    }
    router.push(url, undefined, { shallow: true });
  };

  const searchResults = useMemo(() => {
    if ((searchTerm.length >= 3 && !searchResultsPages) || !data) return;

    return getProductsWithFixedCategory(
      searchTerm.length >= 3
        ? searchResultsPages?.pages
            ?.flatMap(page => page.data?.products)
            .map(prod => {
              return enhanceProductWithSlug(prod);
            })
        : data?.pages
            ?.flatMap(page => page)
            .map(prod => {
              return enhanceProductWithSlug(prod);
            }),
      categoriesFromFile,
      productTags,
      bundleOffersSummary,
      router.locale,
      undefined,
      personalizedProductsIds,
      a
    );
  }, [searchResultsPages, data, bundleOffersSummary, categoriesFromFile, productTags, router.locale, productsOrder]);

  useEffect(() => {
    if ((searchTerm.length >= 3 && !searchResultsPages) || !data) return;

    setTotalCount(
      searchTerm.length >= 3
        ? searchResultsPages?.pages?.[0]?.totalCount
        : data?.pages?.reduce((sum, page) => sum + page.length, 0)
    );
  }, [searchResultsPages, data, searchTerm.length]);

  const lastResultItemRef = useCallback(
    node => {
      if (searchTerm < 3) {
        if (!node) return;
        if (isLoadingProducts || isFetchingNextPageProducts) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting) {
            fetchNextPageProducts();
          }
        });

        observer.current.observe(node);
      } else {
        if (!node) return;
        if (isLoadingCatalogueSearch || isFetchingNextPageSearch) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting) {
            fetchNextPageSearch();
          }
        });

        observer.current.observe(node);
      }
    },
    [
      isLoadingProducts,
      isFetchingNextPageProducts,
      fetchNextPageProducts,
      isLoadingCatalogueSearch,
      isFetchingNextPageSearch,
      fetchNextPageSearch,
    ]
  );

  return (
    <div className={styles.searchCatalogue}>
      <CategoryTitle title={titleCaseString(brand)} />
      <div className={styles.mobileFab}>
        <ScrollToTop />

        <FiltersFAB
          onClick={() => {
            setIsFiltersSheetOpen(true);
          }}
        />
      </div>
      <div className={styles.fab}>
        <ScrollToTop />
      </div>
      <div className={styles.searchBarChipsContainer}>
        <div>
          <ExpandableSearchBar
            placeholder={t('common.header.search_placeholder_mobile')}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        </div>
        {filterChipsItems.length > 1 && (
          <div className={styles.chipsContainer}>
            <FilterChipsList
              items={filterChipsItems}
              onChange={handleSelectSubcategory}
              options={{ hideArrows: true }}
            />
          </div>
        )}
      </div>
      <div className={styles.breadCrumbAndSortContainer}>
        <div className={styles.headings}>
          <Heading type={HeadingEnum.HeadingM} bold={BoldEnum.Bold}>
            {totalCount > 0 && `${totalCount} ${t('categories.breadcrumbs.products')}`}
          </Heading>
        </div>
        <div className={styles.filtersWebContainer}>
          <SortingAndFilters
            text={t('category_details.sorting_filters.btn_title')}
            numberOfFilters={numberOfSelectedFilters || undefined}
            onClick={() => {
              setIsFiltersSheetOpen(true);
            }}
          />
        </div>
      </div>
      {(searchTerm.length >= 3 && isLoadingCatalogueSearch) || (searchTerm.length < 3 && isLoadingProducts) ? (
        <ProductCatalogueV2 isLoading products={[]} loaderTilesAmount={5} />
      ) : (
        <>
          <ProductCatalogueV2
            currencyFormatter={formatter}
            title={''}
            products={searchResults}
            offerLabelHasRibbon={process.env.offerLabelHasRibbon}
            isTileDetailsAnimated={process.env.isTileDetailsAnimated}
            ctaIcon={process.env.ctaIcon}
            listIcon={process.env.listIcon}
            lastItemRef={lastResultItemRef}
            ImageComponent={CustomImage}
            onListClick={openListSideSheet}
            checkStatus={checkListStatus}
            addProduct={product =>
              addProduct(
                product,
                false,
                {
                  item_list_id: 'brandPageProducts',
                  item_list_name: 'brandPageProducts',
                },
                false,
                t('common.asides.side_cart.no_comment')
              )
            }
            removeProduct={product =>
              removeProduct(
                product,
                {
                  item_list_id: 'brandPageProducts',
                  item_list_name: 'brandPageProducts',
                },
                t('common.asides.side_cart.no_comment')
              )
            }
            LinkComponent={props => {
              const propsWithCleanUrl = getProductPropsWithCleanUrl(props);
              return (
                <Link
                  prefetch={false}
                  {...propsWithCleanUrl}
                  href={propsWithCleanUrl.href}
                  onClick={() => {
                    const product = getProductBasedOnSlug(searchResults, propsWithCleanUrl.href);
                    if (product) {
                      selectItem(getProductInfo(product.originalValues), {
                        item_list_id: 'brandPageCatalogue',
                        item_list_name: 'brandPageCatalogue',
                        index: getProductIndexBasedOnSlug(propsWithCleanUrl.href) + 1,
                      });
                    }
                  }}
                />
              );
            }}
            onProductDetails={onSelect}
            noItemsFoundImage={EmptyStateImg.src}
            noItemsFoundWording={t('common.empty_state_title')}
            setMaxSnackbar={setShowSnackbar}
            defaultProductImage={defaultProductImage}
            forYouLabel={t('common.user.profile.menu.for_you')}
          />

          {(searchTerm.length >= 3 && isFetchingNextPageSearch) ||
          (searchTerm.length < 3 && isFetchingNextPageProducts) ? (
            <ProductCatalogueV2 isLoading products={[]} loaderTilesAmount={5} />
          ) : null}
        </>
      )}
      <SnackBar open={showSnackBar} setOpen={setShowSnackbar} text={t('common.snackbar')} position={'center'} />
      {showBundleOffersSnackBar && (
        <OffersSnackbar
          emoji="🎉"
          open={showBundleOffersSnackBar}
          setOpen={BundleOfferSnackBarOpen.update}
          hasCloseOption={true}
          position="center"
          type="warning"
          autoWidth={true}
          buttonText={t('common.offers_snack_bar.btn_label')}
          text={getBundleOfferDescription(SelectedBundleOfferStore.getValue(), bundleOffersSummary)}
          onButtonPress={() => {
            setShowBundleOffersSnackBar(false);
            BundleOfferSnackBarOpen.update(false);
            BundleOfferSideSheetStore.update(true);
          }}
          autoHideDuration={8000}
          closeOnClickAway={true}
          hasShadow={true}
        />
      )}
      {isFiltersSheetOpen && (
        <FiltersAside
          isOpen={isFiltersSheetOpen}
          isLoading={isFiltersLoading}
          handleApplyFilters={handleApplyFilters}
          clearFilters={clearFilters}
          handleClose={() => setIsFiltersSheetOpen(false)}
          selectedFilters={selectedFilters}
          setSelectedFilters={handleSelectFilter}
          selectedSort={selectedSort}
          setSelectedSort={setSelectedSort}
          hasClosest
          hideOffers={process.env.hideOffers}
        />
      )}
    </div>
  );
}
